import React, { useState, useMemo, createContext, useContext } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, GlobalStyles } from '@mui/material';
import Layout from './components/Layout';
import Login from './components/Login';
import EncoderDecoder from './apps/EncoderDecoder';
import MigrationEstimator from './apps/MigrationEstimator';
import SmtpTester from './apps/SmtpTester';

import PdfSeparator from './apps/PdfSeparator';
import PdfConverter from './apps/PdfConverter';
import PdfMerger from './apps/PdfMerger';
import OcrExtractor from './apps/OcrExtractor';

// Context to manage color mode
export const ColorModeContext = createContext({ toggleColorMode: () => { } });

function App() {
  const [mode, setMode] = useState('dark');

  // Session timeout constant (30 minutes)
  const SESSION_TIMEOUT = 30 * 60 * 1000;

  // Initialize auth state from local storage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const lastActive = localStorage.getItem('lastActive');
    if (lastActive) {
      const timeDiff = Date.now() - parseInt(lastActive, 10);
      return timeDiff < SESSION_TIMEOUT;
    }
    return false;
  });

  const [activeApp, setActiveApp] = useState('encoder');

  // Session management effect
  React.useEffect(() => {
    if (!isAuthenticated) return;

    // Update last active time
    const updateActivity = () => {
      localStorage.setItem('lastActive', Date.now().toString());
    };

    // Check for timeout
    const checkTimeout = () => {
      const lastActive = localStorage.getItem('lastActive');
      if (lastActive) {
        const timeDiff = Date.now() - parseInt(lastActive, 10);
        if (timeDiff > SESSION_TIMEOUT) {
          setIsAuthenticated(false);
          localStorage.removeItem('lastActive');
        }
      }
    };

    // Initial update
    updateActivity();

    // Event listeners for user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);

    // Interval to check timeout every minute
    const interval = setInterval(checkTimeout, 60000);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const handleLogin = () => {
    localStorage.setItem('lastActive', Date.now().toString());
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('lastActive');
    setIsAuthenticated(false);
  };

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'dark'
            ? {
              // Dark Mode Palette
              primary: { main: '#00f2ff' },
              secondary: { main: '#7b1fa2' },
              background: {
                default: '#0a0e17',
                paper: '#111625',
              },
              text: { primary: '#e2e8f0', secondary: '#94a3b8' },
            }
            : {
              // Light Mode Palette
              primary: { main: '#0078ff' },
              secondary: { main: '#6a1b9a' },
              background: {
                default: '#f8fafc',
                paper: '#ffffff',
              },
              text: { primary: '#1e293b', secondary: '#475569' },
            }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
          h4: { fontWeight: 700, letterSpacing: '-0.5px' },
          h6: { fontWeight: 600 },
          button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.5px' },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'dark' ? 'rgba(17, 22, 37, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)',
                boxShadow: mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.2)' : '0 8px 32px rgba(0, 0, 0, 0.05)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                padding: '10px 24px',
                boxShadow: 'none',
              },
              containedPrimary: {
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #00f2ff 0%, #0078ff 100%)'
                  : 'linear-gradient(135deg, #0078ff 0%, #00c6ff 100%)',
                color: '#fff',
                '&:hover': {
                  boxShadow: mode === 'dark' ? '0 0 20px rgba(0, 242, 255, 0.4)' : '0 0 20px rgba(0, 120, 255, 0.4)',
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiFilledInput-root': {
                  backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                  '&.Mui-focused': { backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                },
              },
            },
          },
        },
      }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            body: {
              background: mode === 'dark'
                ? 'radial-gradient(circle at top right, #1a2c4e 0%, #0a0e17 40%) fixed'
                : 'radial-gradient(circle at top right, #e2e8f0 0%, #f8fafc 40%) fixed',
              transition: 'background 0.3s ease-in-out'
            }
          }}
        />

        {!isAuthenticated ? (
          <Login onLogin={handleLogin} />
        ) : (
          <Layout
            activeApp={activeApp}
            setActiveApp={setActiveApp}
            onLogout={handleLogout}
          >
            {/* App Container with Fade In Animation */}
            <Box sx={{ animation: 'fadeIn 0.6s ease-out', '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
              {activeApp === 'encoder' && <EncoderDecoder />}
              {activeApp === 'migrator' && <MigrationEstimator />}
              {activeApp === 'smtp' && <SmtpTester />}
              {activeApp === 'pdf-separator' && <PdfSeparator />}
              {activeApp === 'pdf-converter' && <PdfConverter />}
              {activeApp === 'pdf-merger' && <PdfMerger />}
              {activeApp === 'ocr' && <OcrExtractor />}
            </Box>
          </Layout>
        )}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;